import { ReactNode, useEffect, useState } from 'react'
import { AIJobStatus, FieldsetCompletion } from 'types'
import { usePollFieldsetCompletion } from 'hooks/queries/workspaces/fieldsetCompletions/usePollFieldsetCompletion'

interface ChildProps {
  fieldsetCompletion?: FieldsetCompletion
}

type PropsWithChildren<P = unknown> = P & {
  // WARNING: This prop can cause re-renders of the entire child tree on change
  children?: (childProps: ChildProps) => ReactNode
}

interface Props {
  fieldsetCompletionId?: number
  onComplete?: (fieldsetCompletion: FieldsetCompletion) => void
}

export const PollFieldsetCompletion = ({ children, fieldsetCompletionId, onComplete }: PropsWithChildren<Props>) => {
  if (fieldsetCompletionId) {
    return (
      <WithFieldsetCompletion fieldsetCompletionId={fieldsetCompletionId} onComplete={onComplete}>
        {children}
      </WithFieldsetCompletion>
    )
  }

  return <WithNoFieldsetCompletion>{children}</WithNoFieldsetCompletion>
}

export const WithNoFieldsetCompletion = ({ children }: PropsWithChildren) => <>{children?.({})}</>

type WithFieldsetCompletionProps = Required<Omit<Props, 'onComplete'>> & Pick<Props, 'onComplete'>

export const WithFieldsetCompletion = ({
  children,
  fieldsetCompletionId,
  onComplete,
}: PropsWithChildren<WithFieldsetCompletionProps>) => {
  const { data } = usePollFieldsetCompletion({ id: fieldsetCompletionId })
  const [isCompleted, setIsCompleted] = useState(false)

  useEffect(() => {
    if (
      !isCompleted &&
      data?.fieldset_completion &&
      [AIJobStatus.Successful, AIJobStatus.Failed].includes(data.fieldset_completion.status)
    ) {
      setIsCompleted(true)
      onComplete?.(data.fieldset_completion)
    }
  }, [data?.fieldset_completion, onComplete, isCompleted])

  return <>{children?.({ fieldsetCompletion: data?.fieldset_completion })}</>
}
